import React, { useState, useEffect, Children } from "react";
import { TabContent } from "./TabContent";
import { TabItem } from "./TabItem";
import { TabsContext } from "./useTabContext";

export type GenericTabsProps = {
  defaultTab?: number | string;
  onChange?: (value: number | string) => void;
  className?: string;
  tiny?: boolean;
  vertical?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  rightElement?: React.ReactNode;
};


// Main GenericTabs component
const GenericTabs: React.FC<GenericTabsProps> & {
  Item: typeof TabItem;
  Content: typeof TabContent;
} = ({
  defaultTab,
  onChange,
  className = "",
  tiny = false,
  vertical = false,
  fullWidth = true,
  children,
  rightElement
}) => {
    const [activeTab, setActiveTab] = useState<number | string>(defaultTab || 0);

    useEffect(() => {
      if (defaultTab !== undefined && defaultTab !== '') {
        setActiveTab(defaultTab);
      }
    }, [defaultTab]);

    const handleTabChange = (value: number | string) => {
      setActiveTab(value);
      if (onChange) {
        onChange(value);
      }
    };

    // Séparer les éléments TabItem des éléments TabContent
    const tabItems: React.ReactElement[] = [];
    const tabContents: React.ReactElement[] = [];

    Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        // @ts-ignore - Nous vérifions le type du composant
        if (child.type === TabItem) {
          tabItems.push(child);
          // @ts-ignore - Nous vérifions le type du composant
        } else if (child.type === TabContent) {
          tabContents.push(child);
        }
      }
    });

    return (
      <TabsContext.Provider value={{
        activeTab,
        setActiveTab: handleTabChange,
        fullWidth,
        tiny // Passage de tiny dans le contexte
      }}>
        <div className={`
          w-full mb-4
          ${vertical ? "flex" : ""}
          ${className}
        `}>
          <ul className={`
            flex list-none p-0 m-0 border-b border-gray-200
            ${vertical ? "flex-col w-64 border-r border-gray-200 border-b-0" : "w-full"}
            ${tiny ? "" : ""}
          `}>
            <div className={`
              flex
              ${vertical ? "flex-col" : ""}
              ${fullWidth ? "w-full" : "w-auto"}
            `}>
              {tabItems.map((item, index) =>
                React.cloneElement(item, {
                  key: item.props.value || index,
                  ...item.props // Préserver toutes les props originales
                })
              )}
              {rightElement && (
                <div className={`
                  flex justify-end
                  ${fullWidth ? "w-full" : "ml-auto"}
                `}>
                  {rightElement}
                </div>
              )}
            </div>
          </ul>

          <div className={vertical ? "flex-1 pl-4" : ""}>
            {tabContents}
          </div>
        </div>
      </TabsContext.Provider>
    );
  };

// Attach sub-components
GenericTabs.Item = TabItem;
GenericTabs.Content = TabContent;

export default GenericTabs;
